import { getRouteProjects } from "@/zapi-frontend/shared/const/router";
import { InnoveraIcon } from "@libs/zapi-ui/assets";
import { Link } from "react-router-dom";

export function TermsOfUsePage({ className = "" }: { className?: string }) {
  return (
    <article className={"mx-auto max-w-3xl px-4 pt-40 pb-10 text-gray-900 " + className}>
      <header
        className={
          "fixed top-0 left-0 w-full flex items-center border-b-[1px] border-innoveraGrayscale-200 gap-4 px-[17px] py-[0px] h-[72px] font-medium bg-white"
        }
      >
        <Link
          data-testid="header-logo-link"
          to={getRouteProjects()}
          className="w-10 h-10 inline-flex justify-center items-center [&_svg>path]:hover:fill-innoveraOrange-400 [&_svg>path]:transition-[fill] [&_svg>path]:duration-[3ms]"
        >
          <InnoveraIcon className="w-6 h-6" />
        </Link>
        <h1 className="text-xl font-bold leading-tight">Terms of Use</h1>
      </header>

      {/* Header */}
      <div className="mb-8 space-y-2">
        <h1 className="text-3xl font-bold leading-tight">Terms of Use</h1>
        <p className="text-sm text-gray-600">
          Last Updated: <time dateTime="2023-12-18">June 2025</time>
        </p>
      </div>

      {/* Intro */}
      <section className="space-y-4">
        <p className="leading-relaxed">
          Please review these terms and conditions (“<strong>Terms</strong>”) carefully.
        </p>
        <p className="leading-relaxed font-semibold">
          THIS IS A LEGALLY BINDING AGREEMENT BETWEEN INNOVERA.AI, INC. (“<strong>INNOVERA</strong>”, “
          <strong>WE</strong>”, “<strong>US</strong>”, AND/OR “<strong>OUR</strong>”) AND YOU. BY USING THE SERVICES
          (DEFINED BELOW) IN ANY MANNER, YOU AGREE THAT YOU HAVE READ AND UNDERSTOOD ALL OF THE TERMS, HAVE TAKEN THE
          TIME AND ASSISTANCE NEEDED TO COMPREHEND THE CONSEQUENCES OF ACCEPTING THIS AGREEMENT, AND AGREE TO BE BOUND
          BY AND A PARTY TO THE TERMS AND CONDITIONS OF THIS AGREEMENT TO THE EXCLUSION OF ALL OTHER TERMS TO THE
          CONTRARY. DO NOT USE THE SERVICES IF YOU ARE UNWILLING OR UNABLE TO BE BOUND BY THIS AGREEMENT.
        </p>
        <p className="leading-relaxed">
          Innovera and you are hereinafter referred to from time to time, individually as “<strong>Party</strong>” and
          collectively as “<strong>Parties</strong>.”
        </p>
      </section>

      {/* 1. About the Services */}
      <section className="mt-8 space-y-4" id="about-the-services">
        <h2 className="text-2xl font-bold">1. About the Services</h2>
        <p className="leading-relaxed">
          We provide our services via our owned and operated website (“<strong>Site</strong>”), our cloud-based
          enterprise innovation management platform (“<strong>Platform</strong>”), and related content, features,
          applications, and other services, including without limitation all online publications, new features, and
          applications (collectively with the Site and Platform, the “<strong>Services</strong>”). Certain services may
          be available via a mobile device, including the ability to browse the Service and the Site from a mobile
          device. Not all Site features may work with all carriers or devices.
        </p>
      </section>

      {/* 2. Acceptance */}
      <section className="mt-8 space-y-4" id="acceptance">
        <h2 className="text-2xl font-bold">2. Acceptance</h2>
        <h3 className="text-lg font-semibold">2.1 Representations.</h3>
        <p className="leading-relaxed">
          By accessing or using our Services, including without limitation, our Site, you accept these Terms of Use
          (“Terms”) as binding and confirm:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>You are of legal age in the jurisdiction in which you reside to form a contract with us;</li>
          <li>You have not been previously suspended or banned from using the Services;</li>
          <li>Your use of the Services is in compliance with any and all applicable laws and regulations; and</li>
          <li>You have the authority to enter into these Terms.</li>
        </ul>
        <h3 className="text-lg font-semibold">2.2 Privacy Notice.</h3>
        <p className="leading-relaxed">
          Our Privacy Notice is hereby incorporated into and forms an integral part of these Terms. Please read this
          notice carefully as it describes how we handle the personal information we collect from and about you in
          connection with your use of and access to the Site and Services. Where processing is based on consent and to
          the extent permitted by law, by entering into these Terms, you consent to Innovera’s collection, use, and
          disclosure of your personal information as described in the Privacy Notice.
        </p>
      </section>

      {/* 3. Modifications */}
      <section className="mt-8 space-y-4" id="modifications">
        <h2 className="text-2xl font-bold">3. Modifications</h2>
        <h3 className="text-lg font-semibold">3.1 Changes to Terms.</h3>
        <p className="leading-relaxed">
          To the extent allowed by law, we reserve the right, in our sole discretion, to modify these Terms or our
          Privacy Notice at any time. You should review these Terms and our Privacy Notice regularly. If we make
          material changes, we will provide you with additional notice, such as posting a statement on our Site or
          sending you an email notification, if we have your email address on file. Your continued use of the Services
          on or after such changes have become effective constitutes acceptance of those changes, to the extent
          permitted by law. We will not have any liability to you for any of the foregoing actions. If you object to any
          such changes, your sole recourse is to stop using our Site and Services.
        </p>
        <h3 className="text-lg font-semibold">3.2 Changes to Service.</h3>
        <p className="leading-relaxed">
          Innovera reserves the right to modify or discontinue the Services (or any part thereof) at any time. This may
          include adding new features, updating existing features, or removing features or functionality from the
          Services, temporarily or permanently. You agree that we will not have any liability to you for the foregoing
          actions. If you object to any such changes, your sole recourse is to stop using the Services. Continued use of
          the Services after the date any such changes take effect constitutes your agreement to such changes.
        </p>
      </section>

      {/* 4. Accessing the Services */}
      <section className="mt-8 space-y-4" id="accessing-the-services">
        <h2 className="text-2xl font-bold">4. Accessing the Services</h2>
        <h3 className="text-lg font-semibold">4.1 General.</h3>
        <p className="leading-relaxed">
          We grant you permission to access and use the Services subject to the restrictions set out in these Terms. It
          is a condition of your use of the Services that the information you provide about yourself is accurate,
          current, and complete and that you maintain and promptly update such information to keep it accurate, current,
          and complete. You may be asked to verify that you are over the applicable age limit to access our Services and
          you hereby agree that you shall not misrepresent your age. We reserve the right, in our sole discretion, to
          refuse, suspend, or terminate your access to the Services upon discovery (or reasonable suspicion) that any
          information you have provided via the Services is not true, accurate, or complete, or otherwise violates these
          Terms.
        </p>
        <h3 className="text-lg font-semibold">4.2 Acceptable Use.</h3>
        <p className="leading-relaxed">
          You may use our Services only for lawful purposes and in accordance with these Terms. We are under no
          obligation to enforce the Terms on your behalf against another user. We encourage you to let us know if you
          believe another user has violated these Terms or otherwise engaged in prohibited or illegal conduct.
        </p>
        <p className="leading-relaxed">
          You agree not to, and will not assist, encourage, or enable others to use our Services as follows:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>For any commercial purpose, except as expressly permitted under these Terms.</li>
          <li>For competitive analysis or to build competitive products.</li>
          <li>
            To violate any applicable national, regional, federal, state, local, or international law or regulation.
          </li>
          <li>
            To create, send, knowingly receive, display, transmit, upload, download, use, or reuse any material which:
            <ul className="mt-2 list-[circle] pl-6 space-y-1">
              <li>
                Is, or contains any material which is, defamatory, obscene, indecent, abusive, offensive, harassing,
                violent, hateful, inflammatory, or otherwise objectionable;
              </li>
              <li>
                Infringes any patent, trademark, trade secret, copyright, or other intellectual property rights of any
                other person;
              </li>
              <li>
                Violates or assists in the violation of legal rights (including the rights of publicity and privacy) of
                others or contain any material that could give rise to any civil or criminal liability under applicable
                laws or regulations, or that otherwise may be in conflict with these Terms and/or our Privacy Notice; or
              </li>
              <li>Is likely to deceive or confuse any person.</li>
            </ul>
          </li>
          <li>In violation of these Terms or any other rules or policies posted by us on the Services.</li>
          <li>To reverse engineer any portion of the Services.</li>
          <li>
            To modify, adapt, appropriate, reproduce, distribute, translate, create derivative works or adaptations of,
            publicly display, sell, trade, or in any way exploit the Services, except as expressly authorized by
            Innovera.
          </li>
          <li>
            To remove or modify any copyright, trademark, or other proprietary rights notice that appears on any portion
            of the Services or on any materials printed or copied from the Services.
          </li>
          <li>To record, process, harvest, collect, or mine information about other users.</li>
          <li>
            To access, retrieve, or index any portion of the Services for the purposes of constructing or populating a
            searchable database.
          </li>
          <li>
            To use any robot, spider, or other automatic device, process, or means to access the Services for any
            purpose.
          </li>
          <li>
            To use the Services to transmit any computer viruses, worms, defects, Trojan horses, or other items of a
            destructive nature.
          </li>
          <li>
            To use the Services to violate the security of any computer network, crack passwords, or security encryption
            codes.
          </li>
          <li>
            To remove, circumvent, disable, damage, or otherwise interfere with any security-related features or other
            restrictions of the Services.
          </li>
          <li>
            To attempt to gain unauthorized access to, interfere with, damage, or disrupt any parts of the Services, the
            server on which the Services is stored, or any server, computer, or database connected to the Services.
          </li>
          <li>To attack the Services via a denial-of-service attack or a distributed denial-of-service attack.</li>
          <li>To otherwise attempt to interfere with the proper working of the Services.</li>
        </ul>
        <h3 className="text-lg font-semibold">4.3 Suspension or Termination of Access.</h3>
        <p className="leading-relaxed">
          If we determine (or reasonably believe) that you have breached the acceptable use terms set out in Section 4.2
          above or (i) are using our Services for any unlawful purpose or deceptive purpose; (ii) pose a threat to us or
          our other users; or (iii) are violating the intellectual property rights of Innovera or any third party, we
          reserve the right to suspend or permanently bar you from accessing our Services. We may further notify
          relevant third parties, including law enforcement as applicable. Unauthorized or other misuse of the Services
          may result in criminal and/or civil prosecution under federal state, and local law or applicable regulations.
          If you become aware of misuse of our Services, please contact us at{" "}
          <a href="mailto:compliance@innovera.ai" className="text-blue-500 underline">
            compliance@innovera.ai
          </a>
        </p>
      </section>

      {/* 5. Intellectual Property Rights */}
      <section className="mt-8 space-y-4" id="intellectual-property-rights">
        <h2 className="text-2xl font-bold">5. Intellectual Property Rights</h2>
        <h3 className="text-lg font-semibold">5.1 Services IP.</h3>
        <p className="leading-relaxed">
          Intellectual property rights, titles, and interests in and to all text, images, graphics, logos, button icons,
          photographs, video and audio files, data associated with the Site and any other portion of the Services and
          the selection, arrangement, structure, coordination, and “look and feel” thereof as well as the Innovera name
          and logo belong to Innovera, its licensors, or other content suppliers (“<strong>Services IP</strong>”).
          Unless otherwise expressly authorized herein or by us in writing, you agree not to display, distribute,
          license, perform, publish, reproduce, duplicate, copy, creative derivative works from, modify, sell, resell,
          exploit, transfer, or upload the Services IP or any portion thereof.
        </p>
        <h3 className="text-lg font-semibold">5.2 License to Services IP.</h3>
        <p className="leading-relaxed">
          Subject to your compliance with these Terms, we grant you a limited, non-exclusive, non-sublicensable,
          revocable, non-transferable license to use Services IP solely for your personal, non-commercial use. Any
          rights not expressly granted herein are reserved by Innovera, its licensors, or other content suppliers.
        </p>
        <h3 className="text-lg font-semibold">5.3 User Content.</h3>
        <p className="leading-relaxed">
          With respect to any content or other materials you upload through the Services (“<strong>User Content</strong>
          ”), you represent and warrant that you own all right, title, and interest in and to such content, including,
          without limitation, all copyrights and rights of publicity contained therein, and that you have all required
          rights to post or transmit such content or other materials without violating any third party’s rights. You
          understand and agree that any liability, loss, or damage arising from or relating to any User Content that you
          post, upload, link to, or otherwise make available via the Services is solely your responsibility. By
          uploading User Content, you hereby grant Innovera a non-exclusive, worldwide, royalty-free, fully paid up,
          transferable, sublicensable, perpetual, irrevocable license to copy, display, upload, adapt, perform, publish,
          distribute, store, modify, and otherwise use your User Content in any and all media, form, medium, technology,
          or distribution methods now known or later developed and for any and all purposes.
        </p>
        <h3 className="text-lg font-semibold">5.5 Feedback.</h3>
        <p className="leading-relaxed">
          From time to time, you may provide us with comments, suggestions, or ideas for enhancements or improvements,
          including new features, materials, and other content (“<strong>Feedback</strong>”). Innovera shall have the
          full, unencumbered right, to use, incorporate, and otherwise fully exercise and exploit any such Feedback, for
          any purpose, commercial or otherwise, without notice to you or acknowledgement or compensation to you.
        </p>
        <h3 className="text-lg font-semibold">5.6 Complaints and Copyright Takedown Requests.</h3>
        <p className="leading-relaxed">
          If you believe your intellectual property is being violated via our Services, please submit a complaint and
          request for takedown of specific material to{" "}
          <a href="mailto:compliance@innovera.ai" className="text-blue-500 underline">
            compliance@innovera.ai
          </a>
          . Be sure to provide us with the following information: (i) an electronic or physical signature of the person
          authorized to act on behalf of the owner of the copyright interest; (ii) a description of the copyrighted work
          that you claim has been infringed; (iii) a description of the location on the Site and the material you claim
          is infringing; (iv) your address, telephone number, and email address; (v) a written statement by you that you
          have a good faith belief that the disputed use is not authorized by the copyright owner, its agent, or the
          law; and (vi) a statement by you, made under penalty of perjury, that the above information in your notice is
          accurate and that you are the copyright owner or authorized to act on the copyright owner’s behalf.
        </p>
        <p className="leading-relaxed font-semibold">
          UNDER FEDERAL LAW, IF YOU KNOWINGLY MISREPRESENT THAT ONLINE MATERIAL IS INFRINGING, YOU MAY BE SUBJECT TO
          CRIMINAL PROSECUTION FOR PERJURY AND CIVIL PENALTIES, INCLUDING MONETARY DAMAGES, COURT COSTS, AND ATTORNEYS’
          FEES.
        </p>
        <p className="leading-relaxed">
          Please note that this procedure is exclusively for notifying Innovera that your copyrighted material has been
          infringed. The preceding requirements are intended to comply with Innovera’s rights and obligations under the
          Digital Millennium Copyright Act (“<strong>DMCA</strong>”), including 17 U.S.C. §512(c), but do not constitute
          legal advice. It may be advisable to contact an attorney regarding your rights and obligations under the DMCA
          and other applicable laws.
        </p>
        <p className="leading-relaxed">
          In accordance with the DMCA and other applicable law, Innovera has adopted a policy of blocking, in
          appropriate circumstances, users who are deemed to be repeat infringers. Innovera may also in its sole
          discretion limit access to the Site or any other portion of the Services for any users who infringe any
          intellectual property rights of others, whether or not there is any repeat infringement.
        </p>
      </section>

      {/* 6. Links to Other Websites and Third-Party Content */}
      <section className="mt-8 space-y-4" id="third-party-links">
        <h2 className="text-2xl font-bold">6. Links to Other Websites and Third-Party Content</h2>
        <p className="leading-relaxed">
          We may provide links to third-party websites, services, and applications that are not operated or controlled
          by Innovera. These Terms do not apply the practices of those third parties. The fact that we link to a
          website, service, or application is not an endorsement, authorization, or representation of our affiliation
          with that third party. We encourage you to review the terms of use and privacy policies of any third-party
          service before using such service or providing any personal information to or through them.
        </p>
      </section>

      {/* 7. Consent to Receive Communications in Electronic Form */}
      <section className="mt-8 space-y-4" id="electronic-communications">
        <h2 className="text-2xl font-bold">7. Consent to Receive Communications in Electronic Form</h2>
        <p className="leading-relaxed">
          For all notices and communications relating to these Terms, you (i) consent to receive communications from us
          in electronic form via the email you have submitted; and (ii) agree that these Terms (including all updates
          thereto), agreements, notices, disclosures, and other communications that we may provide to you electronically
          satisfy any legal requirement that such communications would satisfy if they were in writing. The foregoing
          does not affect your non-waivable rights.
        </p>
        <p className="leading-relaxed">
          We may also use your email address to send you other messages, including to (i) respond to your requests,
          inquiries, comments, and suggestions; and (ii) to communicate with you about products, services, offers,
          promotions, and events we offer and provide news and information we think will be of interest to you. You can
          opt out of receiving our promotional communications at any time by following the instructions included in
          those communications or by contacting us at{" "}
          <a href="mailto:compliance@innovera.ai" className="text-blue-500 underline">
            compliance@innovera.ai
          </a>
          .
        </p>
      </section>

      {/* 8. Warranty Disclaimer */}
      <section className="mt-8 space-y-4" id="warranty-disclaimer">
        <h2 className="text-2xl font-bold">8. Warranty Disclaimer</h2>
        <p className="leading-relaxed font-semibold">
          THE SERVICES, INCLUDING WITHOUT LIMITATION ALL SERVICES IP MADE AVAILABLE VIA THE SITE OR OTHER SERVICES ARE
          PROVIDED “AS IS” AND “AS AVAILABLE”. WHILE REASONABLE EFFORTS HAVE BEEN MADE TO ENSURE SUCH MATERIALS ARE
          ACCURATE AND RELIABLE WHEN POSTED, THE MATERIALS MIGHT NOT BE ACCURATE, COMPLETE, CURRENT, OR ERROR-FREE. ALL
          CONTENT IS PROVIDED FOR INFORMATION PURPOSES ONLY. THE RELIANCE ON ANY INFORMATION PROVIDED THROUGH THE
          SERVICES IS SOLELY AT YOUR OWN RISK. TO THE FULLEST EXTENT PERMISSIBLE BY APPLICABLE LAW, WE DISCLAIM ALL
          TERMS, CONDITIONS, GUARANTIES, REPRESENTATIONS AND WARRANTIES (EXPRESS, IMPLIED, STATUTORY, AND OTHERWISE), IN
          RESPECT OF THE SERVICES, INCLUDING, WITHOUT LIMITATION, THOSE OF MERCHANTABILITY, NON-INFRINGEMENT, TITLE,
          QUALITY, AND FITNESS FOR A PARTICULAR PURPOSE. WE MAKE NO GUARANTEE THAT THE QUALITY OF ANY PRODUCTS,
          SERVICES, INFORMATION, OR OTHER MATERIAL ACCESSIBLE THROUGH THE SERVICES WILL MEET YOUR EXPECTATIONS. INNOVERA
          SHALL NOT BE LIABLE FOR DELAY OR FAILURE IN PERFORMANCE RESULTING FROM CAUSES BEYOND INNOVERA’S REASONABLE
          CONTROL, INCLUDING, WITHOUT LIMITATION, DELAYS AND OTHER PROBLEMS INHERENT IN THE USE OF THE INTERNET AND
          ELECTRONIC COMMUNICATIONS.
        </p>
      </section>

      {/* 9. Indemnification */}
      <section className="mt-8 space-y-4" id="indemnification">
        <h2 className="text-2xl font-bold">9. Indemnification</h2>
        <p className="leading-relaxed">
          You agree to release, indemnify, and hold harmless Innovera against all losses, damages, expenses (including
          reasonable attorney’s fees, rights, claims, actions of any kind and injury (including death) made or brought
          against us by a third party arising out of (i) your breach of these Terms; (ii) your violation of applicable
          laws; (iii) your violation of the rights of any third party, including intellectual property rights; and (ii)
          your negligence or willful misconduct.
        </p>
      </section>

      {/* 10. Limitation of Liability */}
      <section className="mt-8 space-y-4" id="limitation-of-liability">
        <h2 className="text-2xl font-bold">10. Limitation of Liability</h2>
        <p className="leading-relaxed">
          INNOVERA ASSUMES NO RESPONSIBILITY OR LIABILITY WHATSOEVER ARISING OUT OF OR IN CONNECTION WITH (I) CONTENT
          POSTED BY ANY USER OR OTHER THIRD PARTY, (II) ANY THIRD-PARTY WEBSITE, PRODUCT, OR SERVICES INTEGRATED IN,
          LINKED TO, OR OTHERWISE ACCESSIBLE THROUGH THE SERVICES, OR (III) THE QUALITY OR CONDUCT OF ANY THIRD PARTY OR
          OTHER USER YOU ENCOUNTER IN CONNECTION WITH YOUR USE OF THE SERVICES. YOU AGREE THAT TO THE MAXIMUM EXTENT
          PERMITTED BY LAW, INNOVERA WILL NOT BE LIABLE TO YOU UNDER ANY THEORY OF LIABILITY. WITHOUT LIMITING THE
          GENERALITY OF THE FOREGOING, YOU AGREE THAT INNOVERA SPECIFICALLY WILL NOT BE LIABLE FOR ANY INDIRECT,
          INCIDENTAL, CONSEQUENTIAL, SPECIAL, OR EXEMPLARY DAMAGES, LOSS OF PROFITS, BUSINESS INTERRUPTION, REPUTATIONAL
          HARM, OR LOSS OF DATA, EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES OR SUCH DAMAGES ARE
          FORESEEABLE) ARISING OUT OF OR IN ANY WAY CONNECTED WITH YOUR USE OF, OR INABILITY TO USE, THE SERVICE. YOUR
          SOLE REMEDY FOR DISSATISFACTION WITH THE SERVICES IS TO STOP USING THE SERVICES. BECAUSE SOME JURISDICTIONS DO
          NOT ALLOW THE EXCLUSION OR LIMITATION OF LIABILITY FOR CONSEQUENTIAL OR INCIDENTAL DAMAGES, IN SUCH
          JURISDICTIONS LIABILITY IS LIMITED TO THE GREATEST EXTENT PROVIDED BY LAW.
        </p>
      </section>

      {/* 11. Miscellaneous */}
      <section className="mt-8 space-y-4" id="miscellaneous">
        <h2 className="text-2xl font-bold">11. Miscellaneous</h2>
        <h3 className="text-lg font-semibold">11.1 Survival.</h3>
        <p className="leading-relaxed">
          If any part of these Terms is held invalid or unenforceable, that portion of the Terms will be, to the maximum
          extent possible, construed consistent with applicable law. The remaining portions shall remain in full force
          and effect. Any failure on the part of Innovera to enforce any provision of these Terms will not be considered
          a waiver of our right to enforce such provision. Our rights under these Terms will survive any termination of
          these Terms to the extent necessary to give full effect to their intended purpose.
        </p>
        <h3 className="text-lg font-semibold">11.2 Choice of Law.</h3>
        <p className="leading-relaxed">
          These Terms and your use of the Services are governed by the federal and state laws of the State of California
          without regard to its conflict of law provisions. You hereby consent to the exclusive jurisdiction and venue
          in any federal or state court of competent jurisdiction located in Santa Clara County for the adjudication of
          any disputes arising from or relating to the Services or these Terms of Use.
        </p>
      </section>

      {/* 12.3–12.6 as numbered in source */}
      <section className="mt-8 space-y-4" id="assignment-entire-agreement-notices">
        <h3 className="text-lg font-semibold">12.3 Assignment.</h3>
        <p className="leading-relaxed">
          We may assign or delegate these Terms, in whole or in part, to any person or entity at any time with or
          without your consent. You may not assign or delegate any rights or obligations under the Terms or Privacy
          Notice without our prior written consent, and any unauthorized assignment and delegation by you shall be void.
        </p>
        <h3 className="text-lg font-semibold">12.4 Entire Agreement.</h3>
        <p className="leading-relaxed">
          These Terms, together with the Privacy Notice, represent the complete and exclusive understanding of the
          agreement between us and you regarding the Services and supersedes any other prior or contemporaneous
          agreement, whether oral or written, and any other communications between us and you relating to the Services.
        </p>
        <h3 className="text-lg font-semibold">12.5 Notices.</h3>
        <p className="leading-relaxed">
          All notices permitted or required under these Terms must be sent in writing (i) if to you by us, then via
          email to any address you have provided, and (ii) if to us by you, then via{" "}
          <a href="mailto:compliance@innovera.ai" className="text-blue-500 underline">
            compliance@innovera.ai
          </a>
          . Notices will be deemed given if (a) if to you, when emailed, and (b) if to us, on receipt by us.
        </p>
        <h3 className="text-lg font-semibold">12.6 Contact Us.</h3>
        <p className="leading-relaxed">
          If you have comments or questions or would like to report violations of these Terms, please contact us at{" "}
          <a href="mailto:compliance@innovera.ai" className="text-blue-500 underline">
            compliance@innovera.ai
          </a>
          .
        </p>
      </section>
    </article>
  );
}
